import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SITE_URL } from "../App";

// Additional duty rates above the pre-2025 MFN baseline, verified July 25, 2026
// against USTR / White House proclamations and trade-law analyses. Update these
// when the regime changes. Last inflection: July 24, 2026 — Section 122's 10% global
// surcharge expired by statute and was replaced the same minute by Section 301
// forced-labor duties (12.5% on the 46 economies without a forced-labor import ban,
// 10% on those that have one), per the July 23, 2026 presidential memorandum.
// Newest layer: the Section 232 pharmaceutical proclamation of April 2, 2026 — a 100%
// duty on patented pharmaceuticals and ingredients, live since July 31, 2026 for the
// companies in the proclamation's Annex III and from September 29, 2026 for everyone
// else, with 15% for Japan/EU/Korea/Switzerland, 10% for the UK, and 0% for generics.
// Newest layer: Section 338 duties on Canada — 50% on a listed set of Canadian goods,
// in effect since just after midnight ET on August 22, 2026, when US-Canada trade talks
// collapsed and the three-day pause expired. The 50% applies whether or not the goods
// qualify under USMCA, so USMCA origin is no longer automatically 0% for listed products.
// Next layer: Section 232 duties on unmanned aircraft systems — proclamation signed
// August 13, 2026, most rates effective September 3, 2026 (some lines deferred to
// February 9, 2027). 100% on drones over 25 kg maximum takeoff weight, thermal-imaging
// drones, docking stations and listed critical components; 25% on specified drones of
// 25 kg or under; 15% for the EU, Japan, Liechtenstein, South Korea, Switzerland and
// Taiwan and 10% for the UK where substantially all hardware, software and technology
// originates there.
const RATES_VERIFIED = "August 30, 2026";

const ORIGINS = [
  { id: "china", label: "China" },
  { id: "eu", label: "European Union" },
  { id: "usmca", label: "Canada / Mexico (USMCA-qualifying, not on the 50% list)" },
  { id: "canada50", label: "Canada — on the 50% list (hockey gear, cement, plywood, liquor, wine, dairy)" },
  { id: "other", label: "Most other countries (Japan, Vietnam, India...)" },
];

const CATEGORIES = [
  { id: "general", label: "General goods (electronics, clothing, toys...)" },
  { id: "auto", label: "Car or auto parts" },
  { id: "metals", label: "Steel, aluminum or copper products" },
  { id: "pharma", label: "Patented brand-name medicine" },
  { id: "drone", label: "Drone or drone parts (rates start September 3, 2026)" },
];

// Additional duty on general goods in effect now (post–July 24, 2026): the 10% global
// Section 122 surcharge expired and was replaced by Section 301 forced-labor duties —
// 12.5% on most economies, layered on top of China's surviving legacy 301 duties.
// USMCA-qualifying general goods from Canada/Mexico stay at preferential (often 0) rates.
const GENERAL_RATES = {
  china: { now: 35 },
  eu: { now: 15 },
  usmca: { now: 0 },
  canada50: { now: 50 },
  other: { now: 12.5 },
};

const SCENARIOS = [
  { id: "now", title: "In effect now", sub: "since July 24, 2026" },
];

function money(n) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function estimate(price, origin, category, scenario) {
  if (!price || price <= 0) return null;
  if (category === "metals") {
    // Section 232: 25–50% depending on the product; runs on its own authority, no expiry.
    return { low: price * 0.25, high: price * 0.5, rateLabel: "25–50% (Section 232)" };
  }
  if (category === "pharma") {
    // Section 232 pharmaceuticals (proclamation of April 2, 2026): 100% default on
    // patented drugs and ingredients — live July 31, 2026 for Annex III companies,
    // September 29, 2026 for the rest. Japan/EU/Korea/Switzerland pay 15%.
    // Generics, biosimilars and US-origin product are exempt at 0%.
    const rate = origin === "eu" ? 15 : 100;
    return {
      low: (price * rate) / 100,
      high: (price * rate) / 100,
      rateLabel: `${rate}% (Section 232 pharma${origin === "eu" ? ", EU tier" : ""})`,
    };
  }
  if (category === "drone") {
    // Section 232 UAS proclamation of August 13, 2026, live September 3, 2026:
    // 100% on drones over 25 kg, thermal-imaging drones, docking stations and listed
    // critical components; 25% on specified drones of 25 kg or under. Allied tier of
    // 15% (EU, Japan, Liechtenstein, South Korea, Switzerland, Taiwan) where
    // substantially all hardware, software and technology originates there.
    if (origin === "eu") {
      return { low: price * 0.15, high: price * 0.15, rateLabel: "15% (Section 232 UAS, allied tier)" };
    }
    return { low: price * 0.25, high: price * 1.0, rateLabel: "25–100% (Section 232 UAS)" };
  }
  if (category === "auto") {
    // Section 232 autos: 25% everywhere, EU capped at 15% by the trade deal; no expiry.
    if (origin === "canada50") {
      // Section 338 proclamation of July 20, 2026 (motor vehicles), live August 22, 2026:
      // an extra 50% on top of the Section 232 auto duty, regardless of USMCA eligibility.
      return { low: price * 0.75, high: price * 0.75, rateLabel: "25% (Section 232) + 50% (Section 338)" };
    }
    const rate = origin === "eu" ? 15 : 25;
    return { low: (price * rate) / 100, high: (price * rate) / 100, rateLabel: `${rate}% (Section 232${origin === "eu" ? ", EU deal cap" : ""})` };
  }
  const rate = GENERAL_RATES[origin][scenario];
  return { low: (price * rate) / 100, high: (price * rate) / 100, rateLabel: `${rate}%` };
}

const TOOL_FAQ = [
  {
    q: "Who actually pays tariffs?",
    a: "The importer of record pays the duty when goods clear US customs. How much of that reaches your receipt varies by product and retailer — this calculator assumes the full duty is passed through to you, which is the upper bound.",
  },
  {
    q: "What changed on July 24, 2026?",
    a: "The 10% global tariff (Section 122) hit its 150-day statutory limit and expired at 12:01 a.m. EDT on July 24, 2026. It did not disappear into nothing: a July 23, 2026 presidential memorandum brought Section 301 forced-labor duties into effect the same minute — 12.5% on the 46 economies that have neither banned forced-labor imports nor committed to, and 10% on those that have, covering about 60 economies in all. Sector tariffs on autos, steel, aluminum and copper (Section 232) and China's legacy Section 301 duties were unaffected, so for most importers landed costs did not actually fall.",
  },
  {
    q: "Is there a 100% tariff on medicine now?",
    a: "On patented brand-name drugs, yes — in stages. The Section 232 proclamation signed April 2, 2026 sets a 100% duty on patented pharmaceuticals and pharmaceutical ingredients, effective July 31, 2026 for the companies listed in the proclamation's Annex III and September 29, 2026 for everyone else. Generics, biosimilars, US-origin product, orphan drugs, nuclear medicines, plasma-derived therapies, fertility treatments and cell and gene therapies are set at 0%. Japan, the EU, South Korea and Switzerland/Liechtenstein pay 15%; the UK pays 10%. A company with a Commerce-approved onshoring plan pays 20%, and one with both an onshoring plan and a most-favored-nation pricing agreement with HHS pays 0% until January 20, 2029.",
  },
  {
    q: "Do the 50% Canada tariffs apply to USMCA goods?",
    a: "Yes, for the products on the list. The Section 338 duties took effect just after midnight ET on Saturday, August 22, 2026, after US-Canada trade talks collapsed the night before, and they apply whether or not the goods qualify under USMCA. Reported coverage is about US$20 billion of Canadian imports (AP, August 22, 2026), with Bloomberg citing a figure nearer US$28 billion — hockey sticks and hockey gear, cement, plywood, liquor and wine, dairy, electrical equipment, machinery, textiles and furniture. Energy, potash, fish, critical minerals and goods already covered by Section 232 are carved out. Prime Minister Mark Carney said Canada will match the duties \"dollar for dollar\"; the Canadian counter-list had not been published when these rates were verified.",
  },
  {
    q: "Are there tariffs on drones in 2026?",
    a: "Yes, from September 3, 2026. A Section 232 proclamation signed August 13, 2026 (\"Adjusting Imports of Unmanned Aircraft Systems and Unmanned Aircraft Systems Components\") sets a 100% duty on specified unmanned aircraft systems — drones with a maximum takeoff weight above 25 kg, drones with thermal imaging, UAS docking stations and certain listed critical components — and 25% on specified drones of 25 kg or under. Where substantially all the hardware, software and technology originates in the EU, Japan, Liechtenstein, South Korea, Switzerland or Taiwan the rate is 15%, and 10% for the United Kingdom. A subset of lines is deferred to February 9, 2027 to allow domestic production to scale up.",
  },
  {
    q: "How accurate are these estimates?",
    a: "They are estimates of the additional duties layered on since 2025, above the normal (MFN) baseline rates that predate them. Exact duties depend on the product's HTS classification, rules of origin, and exemptions. Rates verified " + RATES_VERIFIED + ".",
  },
];

export default function TariffCalculator() {
  const navigate = useNavigate();
  const [price, setPrice] = useState("1000");
  const [origin, setOrigin] = useState("china");
  const [category, setCategory] = useState("general");

  const priceNum = parseFloat(price) || 0;
  const canonicalUrl = `${SITE_URL}/tools/tariff-calculator/`;
  const metaDescription =
    "Free calculator: how much Trump's 2026 tariffs add to what you buy — by country of origin and product type, at the rates in effect now after the July 24 Section 301 switch.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Tariff Cost Calculator",
    url: canonicalUrl,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    publisher: { "@type": "Organization", name: "Between the News" },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: TOOL_FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const sectorNote =
    origin === "canada50"
      ? "The 50% Section 338 duties on listed Canadian goods took effect just after midnight ET on August 22, 2026, when trade talks collapsed. They apply regardless of USMCA eligibility. Energy, potash, fish, critical minerals and goods already under Section 232 are carved out — pick the USMCA option for those."
      : category === "drone"
      ? "Section 232 drone duties (proclamation of August 13, 2026) start September 3, 2026. The 100% rate hits drones over 25 kg at maximum takeoff weight, thermal-imaging drones, docking stations and listed critical components; specified drones of 25 kg or under are at 25%. The EU, Japan, Liechtenstein, South Korea, Switzerland and Taiwan get a 15% tier, and the UK 10%, where substantially all hardware, software and technology originates there. Some lines are deferred to February 9, 2027."
      : category === "pharma"
      ? "Section 232 pharmaceutical duties (proclamation of April 2, 2026) hit patented brand-name drugs only — live since July 31, 2026 for the companies in Annex III, September 29, 2026 for the rest. Generics, biosimilars and US-made product are at 0%, and the rate drops to 20% with a Commerce-approved onshoring plan or 0% with an onshoring plan plus an HHS most-favored-nation pricing deal."
      : category !== "general"
      ? "Section 232 sector tariffs run on their own authority with no expiration date — the July 24 Section 122/301 switch didn't touch this product type."
      : null;

  return (
    <div className="tool-page">
      <title>Tariff Cost Calculator: What Trump's 2026 Tariffs Add to Your Purchase — Between the News</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Tariff Cost Calculator — Between the News" />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <button className="detail-back" onClick={() => navigate("/")}>
        {"←"} Home
      </button>

      <div className="tool-header">
        <h1 className="tool-title">Tariff Cost Calculator</h1>
        <p className="tool-subtitle">
          What the 2026 tariffs add to something you buy, at the rates in effect
          now after the July 24 Section 301 switch. Rates verified {RATES_VERIFIED}.
        </p>
      </div>

      <div className="tool-card">
        <label className="tool-label" htmlFor="tool-price">Purchase price (USD)</label>
        <input
          id="tool-price"
          className="tool-input"
          type="number"
          min="0"
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="e.g. 1000"
        />

        <label className="tool-label" htmlFor="tool-origin">Where is it made?</label>
        <select id="tool-origin" className="tool-select" value={origin} onChange={(e) => setOrigin(e.target.value)}>
          {ORIGINS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>

        <label className="tool-label" htmlFor="tool-category">What kind of product?</label>
        <select id="tool-category" className="tool-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="tool-results">
        {SCENARIOS.map((s) => {
          const est = estimate(priceNum, origin, category, s.id);
          return (
            <div className="tool-scenario" key={s.id}>
              <div className="tool-scenario-title">{s.title}</div>
              <div className="tool-scenario-sub">{s.sub}</div>
              <div className="tool-scenario-value">
                {est
                  ? est.low === est.high
                    ? `+${money(est.low)}`
                    : `+${money(est.low)} to +${money(est.high)}`
                  : "—"}
              </div>
              <div className="tool-scenario-rate">{est ? est.rateLabel : "enter a price"}</div>
            </div>
          );
        })}
      </div>

      {sectorNote && <div className="tool-note">{sectorNote}</div>}

      <div className="tool-explainer">
        <p>
          These are the additional duties layered on since 2025, above the normal
          (MFN) baseline — assuming the importer passes the full cost to you, which
          is the upper bound. The 10% global Section 122 tariff expired at 12:01 a.m.
          EDT on July 24, 2026 at its 150-day statutory limit, and was replaced the
          same minute by Section 301 forced-labor duties — 12.5% on most economies,
          10% on those with a forced-labor import ban — under the July 23, 2026
          presidential memorandum. China&apos;s ~35% combined duties and the Section 232
          sector tariffs (autos 25%, steel/aluminum/copper 25–50%) were untouched, so
          most landed costs did not fall. Since July 31, 2026 there is one more layer:
          a 100% Section 232 duty on patented brand-name medicine and its ingredients,
          phasing in company by company through September 29, 2026. And since just after
          midnight ET on August 22, 2026 there is a Canada layer: 50% Section 338 duties on
          roughly US$20 billion of listed Canadian goods, applying whether or not the product
          qualifies under USMCA, after the two governments failed to sign the deal Trump had
          announced four days earlier.
        </p>
        <p>
          For the full picture — every rate in effect, the legal fight underneath,
          and what replaces the expiring tariff —{" "}
          <a href="/analysis/257/trump-tariffs-in-effect-right-now-every-rate-the-50-canada-d/">
            see our continuously updated tariff tracker
          </a>
          {" — or browse "}
          <a href="/trackers/">all our live trackers and tools</a>.
        </p>
      </div>

      <div className="faq-section">
        <div className="faq-header">{"❓"} Frequently Asked Questions</div>
        {TOOL_FAQ.map((f, i) => (
          <div className="faq-item" key={i}>
            <div className="faq-q">{f.q}</div>
            <div className="faq-a">{f.a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
